import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { COLLAR_COLORS } from "@petloc/shared";

type Color = (typeof COLLAR_COLORS)[number];

const COLOR_HEX: Record<Color, string> = {
  red: "#dc2626",
  blue: "#2563eb",
  green: "#16a34a",
  yellow: "#eab308",
  pink: "#ec4899",
  black: "#111827",
  orange: "#f97316",
  purple: "#9333ea",
};

interface Props {
  value: Color;
  onChange: (c: Color) => void;
  className?: string;
}

export function ColorSwatch({ color, size = 20 }: { color: Color; size?: number }) {
  return (
    <span
      className="inline-block rounded-full border border-slate-300 shadow-inner shrink-0"
      style={{ width: size, height: size, background: COLOR_HEX[color] }}
    />
  );
}

export function ColorSelect({ value, onChange, className }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState<number>(COLLAR_COLORS.indexOf(value));
  const ref = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) setHighlight(Math.max(0, COLLAR_COLORS.indexOf(value)));
  }, [open, value]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open && (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % COLLAR_COLORS.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h - 1 + COLLAR_COLORS.length) % COLLAR_COLORS.length);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const c = COLLAR_COLORS[highlight];
      if (c) {
        onChange(c);
        setOpen(false);
      }
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  return (
    <div ref={ref} className={`relative ${className ?? ""}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        onKeyDown={onKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 bg-white hover:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-500 min-w-[9rem] w-full"
      >
        <ColorSwatch color={value} />
        <span className="flex-1 text-left text-sm text-slate-800">{t(`collarColors.${value}`)}</span>
        <svg
          className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {open && (
        <div
          ref={listRef}
          role="listbox"
          className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 max-h-64 overflow-auto py-1"
        >
          {COLLAR_COLORS.map((c, idx) => {
            const selected = c === value;
            const highlighted = idx === highlight;
            return (
              <button
                key={c}
                type="button"
                role="option"
                aria-selected={selected}
                onMouseEnter={() => setHighlight(idx)}
                onClick={() => {
                  onChange(c);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                  highlighted ? "bg-brand-50" : ""
                } ${selected ? "font-medium" : ""}`}
              >
                <ColorSwatch color={c} />
                <span className="flex-1 text-left text-slate-800">{t(`collarColors.${c}`)}</span>
                {selected && <span className="text-brand-600">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
